import {
  CometChatCalls,
  type SessionSettings,
} from '@cometchat/calls-sdk-javascript';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import {
  CometChatMessageEvents,
  CometChatUIKitLoginListener,
} from '@cometchat/chat-uikit-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { COMETCHAT_CONSTANTS } from './AppConstants';
import ChatDemo from './ChatDemo';
import JoinSession from './JoinSession';
import WaitingForParticipants from './components/WaitingForParticipants';
import { auth } from './firebase';
import { getRandomMeetingId } from './utils/common';
import { playSound } from './utils/sound';

// LogRocket tracking helper (defined in index.html)
declare global {
  interface Window {
    trackCallSession?: (
      sessionId: string,
      userId: string,
      userName: string,
    ) => void;
  }
}

interface HomeProps {
  onLogout: () => void;
}

function Home({ onLogout }: HomeProps) {
  const [isStarted, setIsStarted] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [meetingName, setMeetingName] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const playSoundOnNewMessage = useRef(true);

  // Group management state
  const [currentGroup, setCurrentGroup] = useState<CometChat.Group | null>(
    null,
  );
  // Waiting UI state
  const [showWaitingUI, setShowWaitingUI] = useState(false);
  const waitUIOpenedRef = useRef(false);
  // Unread message count state
  const [, setUnreadCount] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isChatOpen) {
      playSoundOnNewMessage.current = false;
    } else {
      playSoundOnNewMessage.current = true;
    }
  }, [isChatOpen]);

  // Group management functions
  const createOrJoinGroup = useCallback(
    async (sessionId: string, meetingName: string) => {
      try {
        if (!CometChat.isInitialized()) {
          console.error(
            'CometChat SDK not available. Make sure UIKit is initialized.',
          );
          return;
        }

        try {
          setMeetingName(meetingName);
          const group = await CometChat.getGroup(sessionId);
          // Check if current user has joined the group
          if (!group.getHasJoined()) {
            const joinedGroup = await CometChat.joinGroup(
              sessionId,
              group.getType() as CometChat.GroupType,
              '',
            );
            setCurrentGroup(joinedGroup);
          } else {
            setCurrentGroup(group);
          }
        } catch (error: unknown) {
          // Group doesn't exist, create it
          let code: string | undefined;
          if (typeof error === 'object' && error !== null && 'code' in error) {
            code = (error as { code?: string }).code;
          }
          if (code === 'ERR_GUID_NOT_FOUND') {
            const group = new CometChat.Group(
              sessionId,
              meetingName,
              CometChat.GROUP_TYPE.PUBLIC,
              undefined,
            );
            const createdGroup = await CometChat.createGroup(group);
            setCurrentGroup(createdGroup);
          } else {
            console.error('Error getting group:', error);
          }
        }
      } catch (error) {
        console.error('Error in group management:', error);
      }
    },
    [],
  );

  const handleCopyInviteLink = useCallback(() => {
    console.log(meetingName);
    const inviteLink = `https://calls.${COMETCHAT_CONSTANTS.IS_STAGING ? 'cometchat-staging.com' : 'cometchat.io'}/?sessionId=${sessionId}&meetingName=${encodeURIComponent(meetingName)}`;
    navigator.clipboard
      .writeText(inviteLink)
      .then(() => {
        alert('Invite link copied successfully!');
      })
      .catch(err => {
        console.error('Failed to copy invite link:', err);
      });
  }, [meetingName, sessionId]);

  useEffect(() => {
    // Add event listener for chat button
    const chatButtonUnsub = CometChatCalls.addEventListener(
      'onChatButtonClicked',
      () => {
        setIsChatOpen(prev => {
          const newChatOpen = !prev;
          if (newChatOpen) {
            setUnreadCount(0);
            CometChatCalls.setChatButtonUnreadCount(0);
          }
          return newChatOpen;
        });
      },
    );
    const participantListOpenUnSub = CometChatCalls.addEventListener(
      'onParticipantListButtonClicked',
      () => {
        setIsChatOpen(false);
      },
    );

    const participantListChangedUnsub = CometChatCalls.addEventListener(
      'onParticipantListChanged',
      participants => {
        console.log(
          'Participant list changed:',
          participants.length,
          'participants',
        );

        if (participants.length >= 1) {
          setShowWaitingUI(false);
        } else {
          if (!waitUIOpenedRef.current) {
            setShowWaitingUI(true);
            waitUIOpenedRef.current = true;
          }
        }
      },
    );

    const sessionJoinedUnsub = CometChatCalls.addEventListener(
      'onSessionJoined',
      () => {
        console.log(
          'Session joined - setting up group. Current sessionId:',
          sessionId,
        );
        const currentSessionId = sessionId || `session-${Date.now()}`;
        createOrJoinGroup(currentSessionId, 'CometChat Team Meeting');

        const loggedInUser = CometChatCalls.getLoggedInUser();
        if (window.trackCallSession && loggedInUser) {
          window.trackCallSession(
            currentSessionId,
            loggedInUser.uid || 'unknown',
            loggedInUser.name || 'Unknown User',
          );
        }

        if (!waitUIOpenedRef.current) {
          setShowWaitingUI(true);
          waitUIOpenedRef.current = true;
        }
      },
    );

    const onShareInviteButtonClicked = CometChatCalls.addEventListener(
      'onShareInviteButtonClicked',
      () => {
        handleCopyInviteLink();
      },
    );

    return () => {
      chatButtonUnsub();
      sessionJoinedUnsub();
      participantListOpenUnSub();
      participantListChangedUnsub();
      onShareInviteButtonClicked();
    };
  }, [sessionId, createOrJoinGroup, handleCopyInviteLink]);

  useEffect(() => {
    const joinCallUnsub = CometChatCalls.addEventListener(
      'onSessionJoined',
      () => {
        playSound('join-call');
      },
    );

    CometChatCalls.addEventListener('onParticipantJoined', () => {
      playSound('join-call');
    });

    CometChatCalls.addEventListener('onSessionLeft', () => {
      playSound('leave-call');
    });

    CometChatCalls.addEventListener('onParticipantLeft', () => {
      playSound('leave-call');
    });

    CometChatCalls.addEventListener('onParticipantHandRaised', () => {
      playSound('group-chat-received');
    });

    CometChatCalls.addEventListener('onParticipantStartedScreenShare', () => {
      playSound('group-chat-received');
    });

    return () => {
      joinCallUnsub();
    };
  }, []);

  useEffect(() => {
    const unsub = CometChatCalls.addEventListener('onConnectionClosed', () => {
      setIsStarted(false);
      setShowWaitingUI(false);
      setUnreadCount(0);
      CometChatCalls.setChatButtonUnreadCount(0);
      setIsChatOpen(false);
    });

    return () => {
      unsub();
    };
  }, []);

  const isMessageOfCurrentChat = useCallback(
    (message: CometChat.BaseMessage) => {
      return (
        currentGroup &&
        message.getReceiverId() == currentGroup.getGuid() &&
        message.getSender().getUid() !=
          CometChatUIKitLoginListener.getLoggedInUser()?.getUid()
      );
    },
    [currentGroup],
  );

  useEffect(() => {
    type Subscription = { unsubscribe: () => void } | undefined;
    let textMessageSubscription: Subscription;
    let mediaMessageSubscription: Subscription;

    if (isStarted) {
      textMessageSubscription =
        CometChatMessageEvents.onTextMessageReceived.subscribe(
          (message: CometChat.TextMessage) => {
            if (isMessageOfCurrentChat(message) && !isChatOpen) {
              if (playSoundOnNewMessage.current) {
                playSound('group-chat-received');
                playSoundOnNewMessage.current = false;
              }
              setUnreadCount(prev => {
                const newCount = prev + 1;
                CometChatCalls.setChatButtonUnreadCount(newCount);
                return newCount;
              });
            }
          },
        );
      mediaMessageSubscription =
        CometChatMessageEvents.onMediaMessageReceived.subscribe(
          (message: CometChat.MediaMessage) => {
            if (isMessageOfCurrentChat(message) && !isChatOpen) {
              if (playSoundOnNewMessage.current) {
                playSound('group-chat-received');
                playSoundOnNewMessage.current = false;
              }
              setUnreadCount(prev => {
                const newCount = prev + 1;
                CometChatCalls.setChatButtonUnreadCount(newCount);
                return newCount;
              });
            }
          },
        );
    }
    return () => {
      textMessageSubscription?.unsubscribe();
      mediaMessageSubscription?.unsubscribe();
    };
  }, [isStarted, isMessageOfCurrentChat, isChatOpen]);

  const joinCall = useCallback(
    async (sessionSettings: SessionSettings & { callToken: string | null }) => {
      if (containerRef.current) {
        const sessionIdToJoin = sessionId || getRandomMeetingId();
        window.history.replaceState(
          {},
          document.title,
          `/${sessionIdToJoin}?meetingName=${sessionSettings.title || 'CometChat Team Meeting'}`,
        );
        const { token } = sessionSettings.callToken
          ? { token: sessionSettings.callToken }
          : await CometChatCalls.generateToken(
              sessionIdToJoin || Date.now().toString(36),
            );

        const firebaseUser = auth.currentUser;
        const isCometChatEmail =
          firebaseUser?.email?.endsWith('@cometchat.com');
        CometChatCalls.joinSession(
          token,
          {
            ...sessionSettings,
            title: sessionSettings.title || 'CometChat Team Meeting',
            hideChatButton: false,
            hideShareInviteButton: false,
            // TODO: Remove this
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(window as any).configState,
            hideRecordingButton: !isCometChatEmail,
          },
          containerRef.current,
        );
        setIsStarted(true);

        // Create or join group when call starts
        if (sessionIdToJoin) {
          createOrJoinGroup(
            sessionIdToJoin,
            meetingName || 'CometChat Team Meeting',
          );
        }
      }
    },
    [sessionId, createOrJoinGroup, meetingName],
  );

  return (
    <>
      {!isStarted && (
        <JoinSession
          sessionId={sessionId}
          initialMeetingName={meetingName}
          onSessionIdChange={setSessionId}
          onStartSession={sessionSettings => {
            joinCall(sessionSettings);
          }}
          onLogout={onLogout}
        />
      )}
      <div
        className="call-container__wrapper"
        style={{
          display: isStarted ? 'block' : 'none',
          position: 'relative',
        }}>
        <div
          ref={containerRef}
          className={`call-container-main__wrapper ${isChatOpen ? 'chat-open' : ''}`}
          style={{
            width: isMobile
              ? '100vw'
              : isChatOpen
                ? 'calc(100vw - 360px)'
                : '100vw',
          }}
        />
        {showWaitingUI && sessionId && (
          <WaitingForParticipants
            onCopyInviteLink={handleCopyInviteLink}
            onClose={() => {
              setShowWaitingUI(false);
            }}
          />
        )}
        <div
          className={`chat-demo-container ${isChatOpen ? 'chat-open' : ''}`}
          style={{
            transform: isMobile
              ? undefined
              : isChatOpen
                ? 'initial'
                : 'translateX(100%)',
            display: 'flex',
          }}>
          {currentGroup && (
            <ChatDemo
              onClose={() => {
                setIsChatOpen(false);
                setUnreadCount(0);
                CometChatCalls.setChatButtonUnreadCount(0);
              }}
              group={currentGroup}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default Home;
